import { useMemo, useState } from 'react';
import { formatJMD } from '../../lib/finances';
import { stockistSummary } from '../../lib/stockist';

// Picking the shop, and seeing who they are.
//
// "Bill to" on a stockist invoice is a relationship you return to every month
// for years, not a name you type once — so it selects a profile, and choosing
// one fills the address and terms *and* shows what they're already holding,
// what they've sold, and what they still owe. That context is exactly what
// you want in front of you while deciding how much stock to send next.
//
// All of it is derived from their existing invoices and statements
// (`stockistSummary`), so none of it can drift from the records it describes.
export default function StockistPicker({
  stockists,
  value,
  onChange,
  invoices,
  statementsByInvoice,
  onCreate,
}) {
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [newShop, setNewShop] = useState({ name: '', address: '', email: '', payment_terms: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const selected = stockists.find((s) => s.id === value) || null;

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const active = stockists.filter((s) => s.is_active !== false);
    if (!q) return active;
    return active.filter((s) => s.name.toLowerCase().includes(q));
  }, [stockists, query]);

  const summary = useMemo(
    () => (selected ? stockistSummary(selected.id, invoices, statementsByInvoice) : null),
    [selected, invoices, statementsByInvoice]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newShop.name.trim()) {
      setError('The shop needs a name.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await onCreate({
        name: newShop.name.trim(),
        address: newShop.address.trim() || null,
        email: newShop.email.trim() || null,
        payment_terms: newShop.payment_terms.trim() || null,
      });
      onChange(created.id);
      setCreating(false);
      setNewShop({ name: '', address: '', email: '', payment_terms: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (creating) {
    return (
      <div className="stockist-picker">
        <div className="stockist-new">
          <h4>New stockist</h4>
          <div className="stockist-new-grid">
            <label className="field">
              <span>Shop name</span>
              <input
                value={newShop.name}
                onChange={(e) => setNewShop((s) => ({ ...s, name: e.target.value }))}
                placeholder="Devon House Gift Shop"
              />
            </label>
            <label className="field">
              <span>Address</span>
              <input
                value={newShop.address}
                onChange={(e) => setNewShop((s) => ({ ...s, address: e.target.value }))}
                placeholder="Kingston, Jamaica"
              />
            </label>
            <label className="field">
              <span>Email</span>
              <input
                value={newShop.email}
                onChange={(e) => setNewShop((s) => ({ ...s, email: e.target.value }))}
              />
            </label>
            <label className="field">
              <span>Payment terms</span>
              <input
                value={newShop.payment_terms}
                onChange={(e) => setNewShop((s) => ({ ...s, payment_terms: e.target.value }))}
                placeholder="30 days from month end"
              />
            </label>
          </div>
          {error && <p className="po-error">{error}</p>}
          <div className="stockist-new-actions">
            <button type="button" className="ghost-button" onClick={() => setCreating(false)}>
              Cancel
            </button>
            {/* type="button": this sits inside the invoice form, and a submit
                here would save the invoice instead of the shop. */}
            <button type="button" className="primary-action" disabled={saving} onClick={handleCreate}>
              {saving ? 'Saving…' : 'Add stockist'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stockist-picker">
      {!selected ? (
        <>
          <div className="stockist-search-row">
            <input
              className="stockist-search"
              placeholder="Search your stockists"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button type="button" className="ghost-button" onClick={() => setCreating(true)}>
              + New stockist
            </button>
          </div>
          {matches.length === 0 ? (
            <p className="po-empty">No stockists yet — add the first shop above.</p>
          ) : (
            <ul className="stockist-options">
              {matches.map((shop) => {
                const shopSummary = stockistSummary(shop.id, invoices, statementsByInvoice);
                return (
                  <li key={shop.id}>
                    <button type="button" onClick={() => onChange(shop.id)}>
                      <span className="stockist-option-name">{shop.name}</span>
                      <span className="stockist-option-meta">
                        {shopSummary.stockHeld > 0
                          ? `${shopSummary.stockHeld} units held`
                          : 'No stock out'}
                        {shopSummary.outstanding > 0 && (
                          <em>{formatJMD(shopSummary.outstanding)} owed</em>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      ) : (
        <div className="stockist-selected">
          <div className="stockist-selected-head">
            <div>
              <strong>{selected.name}</strong>
              {selected.address && <span>{selected.address}</span>}
              {selected.email && <span>{selected.email}</span>}
              {selected.payment_terms && <span>Terms: {selected.payment_terms}</span>}
            </div>
            <button type="button" className="ghost-button" onClick={() => onChange(null)}>
              Change
            </button>
          </div>

          <dl className="stockist-figures">
            <div>
              <dt>Previous invoices</dt>
              <dd>{summary.invoiceCount}</dd>
            </div>
            <div>
              <dt>Stock held now</dt>
              <dd>{summary.stockHeld} units</dd>
            </div>
            <div>
              <dt>Total sales</dt>
              <dd>{formatJMD(summary.totalSales)}</dd>
            </div>
            <div>
              <dt>Outstanding</dt>
              <dd className={summary.outstanding > 0 ? 'owed' : undefined}>
                {formatJMD(summary.outstanding)}
              </dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );
}
